// Inline "you've done this workout before" strip rendered atop conditioning
// BlockCards. Surfaces the 3 most recent attempts (date + score/time) plus
// the all-time best so the coach can program with context and the athlete
// instantly knows what to beat.

import React from "react";
import { ClockCounterClockwise, Trophy } from "@phosphor-icons/react";
import { formatDuration } from "@/lib/api";
import { formatStoredDistance } from "@/lib/units";

const fmtTime = (sec) => formatDuration(sec);

// Format an ISO datetime as "May 12" / "May 12, 2025" if not this year.
const fmtShortDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const opts = d.getFullYear() === now.getFullYear()
    ? { month: "short", day: "numeric" }
    : { month: "short", day: "numeric", year: "numeric" };
  return d.toLocaleDateString(undefined, opts);
};

// Pick the right scoring display for the attempt based on block type.
// AMRAP / EMOM → "{rounds}+{extra}"; For Time / cap stopwatch → "M:SS"; both → both.
function formatScore(attempt, blockType) {
  const parts = [];
  if (attempt.total_time_seconds && attempt.total_time_seconds > 0) {
    parts.push(fmtTime(attempt.total_time_seconds));
  }
  if (attempt.rounds_completed && attempt.rounds_completed > 0) {
    const extra = attempt.extra_reps || 0;
    parts.push(extra ? `${attempt.rounds_completed}+${extra}` : String(attempt.rounds_completed));
  }
  if (!parts.length && attempt.calories) parts.push(`${attempt.calories} cal`);
  if (!parts.length && attempt.distance_meters) parts.push(formatStoredDistance(attempt.distance_meters, attempt.distance_unit || "m"));
  if (!parts.length) return null;
  return parts.join(" · ");
}

export default function PriorAttempts({ record, blockType, testId }) {
  if (!record) return null;
  const attempts = (record.attempts || []).slice(0, 3);
  if (!attempts.length) return null;

  // Decide what the all-time best looks like (For Time → best time; AMRAP/EMOM → best rounds).
  const bestTime = record.best_time_seconds
    ? { value: fmtTime(record.best_time_seconds), at: record.best_time_at }
    : null;
  const bestRounds = record.best_rounds
    ? {
        value: record.best_rounds_extra
          ? `${record.best_rounds}+${record.best_rounds_extra}`
          : String(record.best_rounds),
        at: record.best_rounds_at,
      }
    : null;
  const bestCal = record.best_calories ? { value: `${record.best_calories} cal`, at: record.best_calories_at } : null;
  const bestDist = record.best_distance_meters
    ? { value: formatStoredDistance(record.best_distance_meters, record.best_distance_unit || "m"), at: record.best_distance_at }
    : null;
  const best = blockType === "intervals"
    ? (bestTime || bestCal || bestDist)
    : (bestTime || bestRounds);

  return (
    <div
      className="mt-3 rounded-xl border border-sage bg-sage/40 px-3 py-2.5"
      data-testid={testId || "prior-attempts"}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 hs-label text-[10px] text-moss">
          <ClockCounterClockwise size={12} weight="bold" />
          You've done this before
        </div>
        {best && (
          <div className="inline-flex items-center gap-1 font-data text-[11px] font-semibold text-moss" title="All-time best">
            <Trophy size={11} weight="fill" /> {best.value}
            <span className="font-normal text-ink-3 ml-1">· {fmtShortDate(best.at)}</span>
          </div>
        )}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 font-data text-[12px] text-ink leading-snug">
        {attempts.map((a, i) => {
          const score = formatScore(a, blockType);
          if (!score) return null;
          return (
            <div key={a.logged_at + i} className="inline-flex items-center gap-1">
              <span className="text-ink-3">{fmtShortDate(a.logged_at)}</span>
              <span className="font-semibold">{score}</span>
              {a.rpe ? <span className="text-ink-3">· RPE {a.rpe}</span> : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
